// ─────────────────────────────────────────────────────────────
// Omina — core contracts
// The IcpMap JSON is the single target every generator must hit.
// The <IcpMap /> component renders exactly this shape.
// ─────────────────────────────────────────────────────────────

import type { PodcastShow } from "./podcast";

/**
 * The three ranked tracks. Offers, maps and the event classifier all score
 * against exactly these — `goalFit` is `{cust, auth, fund}` and nothing else.
 */
export type Goal = "fund" | "auth" | "cust";

/**
 * Every track a founder can hold, including the one that carries no ranking.
 *
 * "learn" was added because "I went to understand the ecosystem" kept being a
 * real answer with nowhere to go. It deliberately has no fit dimension: an
 * unranked, date-ordered list of rooms is honest, while a list ranked by the
 * buyer ladder would be another track's answer wearing this one's label —
 * rule #2, applied to goals.
 */
export const GOAL_TYPES = ["cust", "auth", "fund", "learn"] as const;
export type GoalType = (typeof GOAL_TYPES)[number];

export const GOAL_STATUSES = ["active", "paused", "completed"] as const;
export type GoalStatus = (typeof GOAL_STATUSES)[number];

/** Narrows to the three tracks that carry a fit score. */
export function isRankedGoal(type: GoalType): type is Goal {
  return type !== "learn";
}

/**
 * A founder profile pinned to a ranked track.
 *
 * The map composer, the draft and the offer ranking all need a `goalFit` key
 * to sort by, and there are three of those. Rather than invent a fourth score
 * for "learn" — which would be a made-up number sitting next to three measured
 * ones — the routes narrow with `isRankedGoal` and hand these generators a
 * profile that is guaranteed to name a track the base actually scores.
 */
export type RankedProfile = FounderProfile & { goal: Goal };

export const GOAL_LABELS: Record<GoalType, { title: string; sub: string }> = {
  fund: { title: "Get funding", sub: "investors" },
  auth: { title: "Build authority", sub: "become the name" },
  cust: { title: "Get customers", sub: "buyers" },
  learn: { title: "Learn", sub: "rooms that teach" },
};

// ── Founder profile (output of the elicitation interview) ────
export interface FounderProfile {
  name: string;
  /** Who you are & what you value — 2-3 sentences, first person */
  who: string;
  /** Product & who you serve — includes stage, buyer, traction */
  product: string;
  /** Which track she is on. Wider than `Goal` because "learn" is a real answer
   *  she can select; the generators that need a ranking narrow it back with
   *  `isRankedGoal` rather than inventing a fourth column of scores. */
  goal: GoalType;
  sector?: string;
  stage?: string;
  location?: string;
  /** The industry they SELL INTO (may differ from their background) */
  targetIndustry?: string;
  /** true when the founder did NOT come from the industry they sell into.
   *  This is the core ICP qualifier: the "industry outsider". */
  industryOutsider?: boolean;

  // ── Who actually buys ──
  //
  // The base of the framework, and for a long time it was one word: the
  // industry. Everything downstream searched, ranked and wrote against that
  // single string, which is why more supply only ever produced more noise —
  // ten thousand events filtered by "fintech" are ten thousand badly filtered
  // events. These three narrow it to a person in a room.
  //
  // All optional: a founder who has not answered them gets the same behaviour
  // as before rather than an empty result. Better a broad search than a
  // confident one built on a blank.
  /** Who signs off — the role, in her words. "Head of risk", "clinic owner". */
  buyerRole?: string;
  /** What kind of company they sit in. "Mid-size brokerages", "Series B SaaS". */
  buyerCompany?: string;
  /** The problem that makes them buy, in their words rather than hers. This is
   *  what a room's description has to echo for the room to be worth an
   *  evening, and what a follow-up has to name to sound like she listened. */
  buyerPain?: string;

  // Elicitation layers (Blocos 1–5). All optional — filled as the
  // interview happens. These are what make output HERS, not generic.
  /** Bloco 1 — rooms/rhythms that energize vs drain her */
  energyPattern?: string;
  /** Bloco 2 — what she does under pressure (e.g. over-talks) */
  pressurePattern?: string;
  /** Bloco 3 — gap between how she's perceived and how she feels */
  perceptionGap?: string;
  /** Bloco 4 — the claim only she can make (from history) */
  claim?: string;
  /** Bloco 5 — her natural register/voice */
  register?: string;
  /** How she opens/presents in a room (element-derived hypothesis, confirmed) */
  presentationPattern?: string;
  /** Operational decision protocol (authority-derived hypothesis, confirmed) —
   *  feeds prep cards: "don't close in the room", etc. */
  decisionProtocol?: string;
  /** Consistent theme (personality Sun gate keynote, confirmed) */
  coreTheme?: string;
  /** Raw interview transcript / notes */
  interviewNotes?: string;
  /** Raw self-knowledge inputs. Doorways for elicitation — NEVER prediction.
   *  The confirmed/corrected language lands in the pattern fields above. */
  personality?: PersonalityInputs;
}

export interface PersonalityInputs {
  enneagram?: string;    // "1".."9" — known, or hypothesized by the interview
  humanDesign?: string;  // generator | mgen | projector | manifestor | reflector
  temperament?: string;  // choleric | sanguine | melancholic | phlegmatic
  /** Four letters, e.g. "INTJ". Self-reported only — nothing computes it, and
   *  nothing here predicts from it (rule #4). It is another doorway to the same
   *  elicitation: a founder who already knows this label has vocabulary for how
   *  she works, and borrowing her vocabulary is the entire point. */
  mbti?: string;
  birthDate?: string;    // ISO date → sun sign/element derived (computed now)
  birthTime?: string;    // HH:mm — unlocks full HD reliability
  birthPlace?: string;   // city, country
  sunSign?: string;
  element?: string;      // fire | earth | air | water
  /** computed by /api/chart */
  hdAuthority?: string;
  hdProfile?: string;
  sunGate?: number;
  throatGates?: number[];
}

/** Output of the discovery interview (chat or call transcript). */
export interface InterviewPatterns {
  energyPattern?: string;
  pressurePattern?: string;
  register?: string;
  presentationPattern?: string;
  /** hypothesis labels with confidence, e.g. "Enneagram 3 (medium confidence)" */
  enneagramHypothesis?: string;
  temperamentHypothesis?: string;
  /** the thing they said they'd never put into words before — the quality check */
  breakthrough?: string;
}

// ── Offer base (curated supply: funds, rooms, pods, communities) ──
export type OfferKind =
  | "fund"
  | "accelerator"
  | "event"
  | "conference"
  | "podcast"
  | "community"
  | "newsletter"
  | "other";

export interface Offer {
  id: string;
  name: string;
  kind: OfferKind;
  /** Which founder-vertical this entry serves (e.g. "fintech", "auto",
   *  "real-estate", "consultant"). Omitted = "fintech" (first seed niche).
   *  Verticals live in DATA, not code — adding a niche = adding rows. */
  vertical?: string;
  /** Display line, e.g. "Seed fund · fintech · writes first check" */
  type: string;
  /** What they care about / thesis */
  thesis: string;
  sectors: string[];
  stages: string[];
  check?: string;
  /** Baseline usefulness per goal, 0–100 */
  goalFit: { cust: number; auth: number; fund: number };
  /** City, or "Online" / "Remote" — powers the location filter on the map */
  location?: string;
  /**
   * How big the audience is, in whoever's own words — "12k listeners an
   * episode", "about 40k subscribers", "unknown".
   *
   * A **string on purpose**, not a number. Nobody publishes an exact listener
   * count, so every real answer here is a range or a claim from a media kit,
   * and storing `12000` would invent a precision the source never gave. Same
   * reasoning as `reachMetrics` on the authority profile.
   *
   * Optional, and absent from every seeded entry today. It is a curation task
   * rather than a code one: a number here has to be looked up and vouched for
   * by a human like everything else in this file (rule #1). The authority
   * surface renders it when it exists and simply omits the line when it does
   * not — which is the honest state until someone does that work.
   */
  reach?: string;
  /** How a founder actually gets in */
  howIn: string;
  source: string;
  verifiedAt: string; // "2026-07"
  /** Debrief-fed conversion signal. null = no data yet (honest default) */
  converted?: number | null;
  notes?: string;
}

// ── The map (what the founder sees) ──────────────────────────
export interface MapItem {
  /**
   * The curated entry this came from, when it came from one.
   *
   * Carried so the UI can look up assets keyed by it — the self-hosted podcast
   * artwork in `apps/app/src/logos/` is named `<offerId>.jpg`. Matching on the
   * display name instead would break the moment a show is renamed in the base,
   * and would silently show the wrong logo rather than none.
   *
   * Absent on items a model composed from a web search, which have no entry
   * behind them. Those fall back to a monogram, which is correct: an unverified
   * find should not wear a logo that implies we vouched for it.
   */
  offerId?: string;
  type: string;
  name: string;
  /** Machine-readable category — drives the per-track filter.
   *  A track shows ONLY its own kinds (see GOAL_KINDS). */
  kind?: OfferKind;
  /** City, or "Online" / "Remote". Powers the location filter. */
  location?: string;
  /** Why this room, with the concrete fact that proves we did the work */
  why: string;
  /** 0–100 overall fit for the selected goal */
  fit: number;
  meters: { cust: number; auth: number; fund: number };
  /** Audience size, carried through from the offer. See `Offer.reach` for why
   *  it is a string and why it is absent from everything today. */
  reach?: string;
  /** "Your angle in —" — the sentence she says when she gets there */
  angle: string;
  source?: string;
  verifiedAt?: string;
  status?: "verified" | "unverified" | "stale";
  /**
   * The resolved show, when this item is a podcast.
   *
   * Carries the niche, the host and the published contact — the four facts
   * that turn "here is a podcast" into "here is who to write to and why".
   * Absent on everything that is not a podcast, and absent on a podcast the
   * directory could not confirm: an unresolved show renders as a name and a
   * link, never as a name with a plausible host beside it.
   */
  show?: PodcastShow;
  /**
   * Cover art, as a path this app serves.
   *
   * Never a third party's url. Two different reasons converge on the same
   * answer: the curated base's artwork is committed under `apps/app/src/logos`
   * so the browser only asks our origin for it, and a scanned show's artwork
   * is proxied by the Worker for the same privacy reason spelled out in
   * `seed/fetch-logos.ts` — a direct request would tell Apple which shows this
   * founder is considering pitching.
   */
  imageUrl?: string;
}

/** Each track shows one category of room — never a mix.
 *  customers → where the ICP physically gathers
 *  authority → media surfaces that reach the market
 *  funding   → capital and the rooms capital shows up in */
export const GOAL_KINDS: Record<Goal, OfferKind[]> = {
  cust: ["conference", "event", "community"],
  // `community` sits on this track as well as on customers, because a Slack
  // room is both places at once: it is where buyers gather AND where a
  // reputation gets built by answering the same question better than anyone
  // else for three months. Which of the two a given room is for is already
  // decided per entry by `goalFit` — a practitioner Slack scores high on cust
  // and low on auth, a room with a stage and a podcast does the reverse — so
  // this list only has to stop being the thing that hides them.
  //
  // It was also an inconsistency: `planFor("auth")` has always searched for
  // communities, so the radar proposed them and the composed map then refused
  // to show them.
  auth: ["podcast", "newsletter", "community"],
  fund: ["fund", "accelerator"],
};

export interface MapCategory {
  label: string;
  items: MapItem[];
}

export interface IcpMap {
  founderName: string;
  goal: Goal;
  /** Sharpened ICP paragraph. May contain <b> tags. */
  icpSummary: string;
  categories: MapCategory[];
  generatedAt: string; // ISO date
  /**
   * The industry this map was composed for.
   *
   * Stamped because the map is a frozen payload while the profile is not: a
   * founder who switches from clinics to SaaS would otherwise keep looking at
   * a page of clinics rooms with nothing to say it is stale. That is rule #2's
   * cross-vertical failure arriving through the back door — not by falling
   * back to another industry's rooms, but by never letting go of her own
   * previous one.
   *
   * Absent on maps generated before this existed; treated as "unknown", which
   * cannot be stale.
   */
  industry?: string | null;
  /** true when this is demo data, not a real generation */
  sample?: boolean;
}

// ── Prep card (the WhatsApp message before a meeting) ────────
export interface MeetingInput {
  person: string;
  org: string;
  when: string; // free text: "Tue 2pm"
  context?: string;
}

export interface PrepCard {
  /** WhatsApp-ready text (with *bold* markers) */
  text: string;
  matchedOfferId?: string;
  generatedAt: string;
}

// ── What a room in the event base is actually for ────────────
/**
 * What one event is good for, read from the event itself.
 *
 * This is the selection layer. Without it the base is a list of everything on
 * in a city, which is close to worthless: a fintech founder chasing customers
 * and a climate founder chasing a seed round need almost disjoint rooms. The
 * pool is shared and the filter is personal — the same shape the curated
 * `offers` base already has, applied one level down.
 *
 * The vertical here describes the *event*, not the calendar it came from. A
 * general SF founders calendar publishes a fintech night and a climate night;
 * inheriting one label from the calendar would have both reach the wrong
 * founder.
 *
 * `null` vertical means it serves any industry, and that is the honest
 * default: most rooms genuinely do, and guessing an industry filters a room
 * away from exactly the person who needed it (rule #2).
 */
export interface EventFit {
  /** Industry this room serves, or null for "any". */
  vertical: string | null;
  /** 0-100 per track. A room can serve two; most serve one well. */
  fit: { cust: number; auth: number; fund: number };
  /** One line, in the founder's terms, for why it ranks — shown on the card
   *  so a ranking is never a number she has to take on faith. */
  why: string;
}

// ── Where a contact was met, and who they are ────────────────
/**
 * The room a connection came from.
 *
 * Only the founder's own taps land here. A *suggested* match — "you were at
 * NerdCon that day" — lives in the UI until she confirms it and is never
 * persisted, because a stored guess is indistinguishable from a fact three
 * months later. `dismissed` is a real answer, and it is what stops the same
 * question being asked forever.
 */
export interface MetAt {
  eventTitle?: string;
  /** ISO date of the event day. */
  eventDate?: string;
  eventLocation?: string | null;
  /** confirmed = she tapped a calendar event · manual = she typed it ·
   *  dismissed = she was asked and skipped. */
  source: "confirmed" | "manual" | "dismissed";
  recordedAt: string; // ISO
}

/**
 * Structured public-web research about one person.
 *
 * Every field is optional and an empty object is a correct, expected result —
 * a person with no public footprint is a normal person, not a failed lookup.
 * `sources` is what makes the rest checkable in one click; nothing renders
 * without it.
 */
/**
 * How useful this person is to the goal she is actually working on.
 *
 * Modelled on `goalMatch` in tracktag.ts, and constrained the same way for the
 * same reasons:
 *
 *  - **`learn` gets no score.** There is no honest way to derive one, exactly
 *    as there is none for a room, and a fourth figure invented to sit beside
 *    three measured ones is rule #2 wearing a meter.
 *  - **No evidence, no score.** A person the web says nothing about is
 *    unresearched, not unsuitable — `null`, never zero.
 *  - **The reasons ship with the number.** A bare percentage is an opinion
 *    wearing a measurement's clothes; each reason names the stated target it
 *    matched and the found fact that matched it, so she can disagree.
 */
/** One dimension of a fit: a number and the sentence that earned it. */
export interface GoalSignal {
  /** 0–100. */
  score: number;
  /** Names the thing she is after and the fact that matches it. Without one,
   *  the dimension is dropped rather than shown as a bare figure. */
  why: string;
}

export interface ContactGoalFit {
  /** The goal this was scored against — never assume it is still her active
   *  one, because she can switch tracks after the research was cached. */
  goal: GoalType;
  /** 0–100. */
  score: number;
  /** Why, in her terms. Empty is not allowed — a score with no reasons is not
   *  a judgement, and the renderer drops the whole block. */
  reasons: string[];
  /**
   * The three ways a person can move a goal forward, scored separately.
   *
   * Each is optional and each is null when the evidence says nothing about it
   * — a dimension nobody has information for is *unknown*, and rendering it as
   * zero would say "this person cannot help you that way", which is a
   * different and unearned claim.
   *
   * `network` is the one to read carefully. Omina has no social graph: rule #5
   * means we never see anyone's connections but hers, so this is an inference
   * from a person's public position ("partner at a fund she named" implies
   * access) and never a lookup of who they actually know. The UI says so.
   */
  signals?: {
    /** Access they plausibly have, inferred from where they sit. */
    network?: GoalSignal | null;
    /** What they know that she needs. */
    knowledge?: GoalSignal | null;
    /** An organisation on her list, now or in their past. */
    company?: GoalSignal | null;
  };
  /** How the score was reached. Only "model" today; the field exists so a
   *  cheaper path can never be mistaken for a read (see `events.fitSource`). */
  basis: "model";
}

export interface ContactEnrichment {
  currentRole?: string;
  /** Where they are now. Separate from `currentRole` because the card shows
   *  "Role @ Company" and a single blob cannot be split back apart reliably. */
  currentCompany?: string;
  /**
   * Ways to reach them — **only when a source states it.**
   *
   * Never assembled from a pattern. A guessed `first@company.com` is the same
   * failure as the guessed `hello@<domain>` the podcast resolver refuses to
   * print, and worse here: this one has her name on the message.
   */
  contactInfo?: {
    email?: string;
    phone?: string;
    website?: string;
    linkedin?: string;
    twitter?: string;
  };
  /** Null whenever it cannot be earned. See `ContactGoalFit`. */
  goalFit?: ContactGoalFit;
  previousRoles?: Array<{ role: string; company: string; when?: string }>;
  education?: string[];
  milestones?: Array<{ what: string; when?: string; url?: string }>;
  /** Present only when the sources say this person invests. Rule #1 holds:
   *  this describes a contact, and never writes into the curated base. */
  investorProfile?: {
    thesis?: string;
    stages?: string[];
    sectors?: string[];
    recentInvestments?: string[];
  };
  sources: string[];
}

// ── Rooms the founder verified from a radar scan ─────────────
/** A radar candidate a human looked at and approved.
 *
 *  This is the other half of rule #1: the scan fills the pipeline, the founder
 *  decides what is real. Approved rooms rank ahead of the curated base on the
 *  map, because someone checked them.
 *
 *  Lived in the prototype's client-only radar.ts. It is a domain type — the
 *  approval is the product — so it belongs here now that rooms are rows. */
export interface ApprovedRoom {
  id: string;
  name: string;
  /** The radar's own vocabulary ("trade-show", "demo-day", "stage"…).
   *  `radarKindToOfferKind()` maps it onto OfferKind for the track filter. */
  kind: string;
  date?: string | null;
  location?: string | null;
  url: string;
  why: string;
  /** The sentence she says when she gets there. */
  angle: string;
  fit: { cust: number; auth: number; fund: number };
  vertical?: string;
  approvedAt: string;
  /** Debrief-fed. null = no data yet (honest default, same as Offer). */
  converted?: number | null;
  /**
   * The show behind a podcast room, resolved at scan time and frozen here.
   *
   * Frozen rather than re-read on every render, because this is the payload
   * she approved: the name she looked at, the host she decided to write to.
   * A refresh is a deliberate act (`POST /api/rooms/:id/refresh`), not a
   * side effect of opening the feed.
   */
  show?: PodcastShow;
  /** The scan's own picture of the room, carried through the approval. It used
   *  to be dropped there, which is why a room that had cover art in the queue
   *  arrived on the map as a monogram. */
  imageUrl?: string | null;
}

// ── Google integrations ──────────────────────────────────────
/**
 * One person on a synced calendar event.
 *
 * Google returns more than this per attendee; what is kept is what answers
 * "who was in that room and did they show up". `self` marks the founder's own
 * row so the list can be rendered without her in it.
 */
export interface GcalAttendee {
  email: string;
  displayName?: string;
  /** Google's vocabulary: "accepted" | "declined" | "tentative" |
   *  "needsAction". */
  responseStatus?: string;
  organizer?: boolean;
  self?: boolean;
}
